import React from "react";
import { commitLocalUpdate } from "react-relay";
import { NavLink, type NavLinkProps } from "react-router-dom";

import cx from "classnames";
import moment from "moment";

import { RecordIconIndicator } from "~/meetingRecorder/RecordIcon";
import { environment } from "~/models";
import { Tooltip } from "~/ui";

import { PrimaryNavLinkUnreadDot } from "../../shared";

import styles from "~/nav/panels/shared/PanelSection.module.css";

export interface MenuLinkProps extends NavLinkProps {
	streamId?: string;
	unread?: boolean;
	icon?: React.ReactNode;
	iconTooltip?: string;
	badge?: React.ReactNode;
	eventGuid?: string;
	showRecordIcon?: boolean;
}

export default function MenuLink({
	streamId,
	showRecordIcon,
	icon: originalIcon,
	iconTooltip,
	unread,
	badge,
	eventGuid,
	className,
	children,
	to,
	...props
}: MenuLinkProps) {
	const didLocalUpdate = React.useRef(false);

	type IsActiveFn = NonNullable<React.ComponentProps<typeof NavLink>["isActive"]>;
	const isActive = React.useCallback<IsActiveFn>(
		(match, location) => {
			let active = Boolean(match);

			if (active && !didLocalUpdate.current) {
				// The last_seen_at field on the NoteStreamUser is updated when we join the
				// websocket group, but we need to manually update it for it to take effect
				// locally.
				if (streamId) {
					// Don't trigger an update while we're rendering
					window.setTimeout(
						() =>
							commitLocalUpdate(environment, store => {
								const stream = store.get(streamId);
								stream?.setValue(moment().format(), "viewerLastSeenAt");
							}),
						0,
					);
				}
				didLocalUpdate.current = true;
			}
			// Checking for Meeting <-> Stream connection
			// If this menu is for a stream and we found an event id for that stream
			// We will use it to match the meeting id in the url.
			// If this menu is for a meeting and we found a stream id for that meeting
			// We will use it to match the stream id in the url.
			if (!active) {
				if (eventGuid && location.pathname.includes("/meetings/")) {
					const urlGuidMatches = location.pathname.match(/\/meetings\/(\w*)\/?/);
					if (!urlGuidMatches || urlGuidMatches.length < 2) {
						return false;
					}
					const urlGuid = urlGuidMatches[1].includes("_") ? urlGuidMatches[1].split("_")[0] : urlGuidMatches[1];
					active = urlGuid == eventGuid;
				} else if (streamId && location.pathname.includes("/stream/")) {
					const urlStreamMatches = location.pathname.match(/\/stream\/((\w|=)*)\/?/);
					if (!urlStreamMatches || urlStreamMatches.length < 2) {
						return false;
					}
					const urlStreamId = urlStreamMatches[1];
					active = urlStreamId == streamId;
				}
			}
			return active;
		},
		[didLocalUpdate, streamId, eventGuid],
	);

	// If `to` is a string, NavLink will convert it to an object anyway, and it
	// will be a new object every time (i.e. unnecessary re-renders).
	const memoizedTo = React.useMemo(() => {
		if (typeof to === "string") {
			return { pathname: to };
		}
		return to;
	}, [to]);

	return (
		<NavLink className={cx(className, styles.menuLink)} to={memoizedTo} {...props} isActive={isActive}>
			{unread ? (
				<span className={styles.iconContainer}>
					<PrimaryNavLinkUnreadDot />
					<RecordIconIndicator checked={showRecordIcon} className={cx(styles.recordIcon, styles.withUnreadDot)} />
				</span>
			) : (
				<Tooltip content={iconTooltip} placement="top">
					<span className={styles.iconContainer}>
						{originalIcon}
						<RecordIconIndicator checked={showRecordIcon} className={styles.recordIcon} />
					</span>
				</Tooltip>
			)}
			{badge}
			{children}
		</NavLink>
	);
}
